import React, { useEffect, useRef } from 'react';

interface Props {
  open: boolean;
  url: string | null;
  stepLabel: string;
  stepIndexHuman: number;
  totalSteps: number;
  onClose: () => void;
}

const StepDrawer: React.FC<Props> = ({
  open,
  url,
  stepLabel,
  stepIndexHuman,
  totalSteps,
  onClose,
}) => {
  // Lock background scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Allow Escape to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // After the iframe loads, inject a small style sheet that hides the
  // global "Ask Jarvis" floating button inside the embed so it doesn't
  // duplicate the one already rendered by the outer app shell. This is a
  // same-origin iframe so accessing contentDocument is allowed.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      // Avoid injecting twice on re-renders / SPA navigations within iframe.
      if (doc.getElementById('smart-router-embed-style')) return;
      const style = doc.createElement('style');
      style.id = 'smart-router-embed-style';
      style.textContent = `
        /* Hide the duplicate floating Ask Jarvis launcher inside the embed. */
        button[title="Ask Jarvis AI"] { display: none !important; }
      `;
      doc.head.appendChild(style);
    } catch (_) {
      // Cross-origin or detached document — nothing to do.
    }
  };

  if (!open || !url) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label={`Smart Router step ${stepIndexHuman} of ${totalSteps}: ${stepLabel}`}
    >
      <iframe
        ref={iframeRef}
        title={stepLabel}
        src={url}
        onLoad={handleIframeLoad}
        className="flex-1 w-full bg-white border-0"
      />
    </div>
  );
};

export default StepDrawer;
